import React, {Key, useEffect, useState} from 'react';
import {FieldProps, wdiCore, WdiModalAppProps} from '@/components/WdiControls';
import {EC} from '@/services/mdm/ec';
import {WdiModal} from '@/components/WdiControls/WdiModalForm';
import {ButtonColumn, FilterColumn, GridFilterForm} from '@/components/GridWidget/GridFilterForm';
import {DatePicker, Input, message} from 'antd';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {SorterResult} from 'antd/lib/table/interface';
import {Enum} from '@/enums/system';
import moment from 'moment';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import {ResponseStatus} from '@/utils/request';
import {toExcel} from '@/components/WdiControls/WdiExcel';
import {Shop} from "@/services/mdm/shop";
import {BudgetProject} from "@/services/sales/bp/budgetProject";
import {FormSpDetail} from "@/services/sales/sp";
import {BOHQ} from "@/services/mdm/bohq";
import {LevelEnum} from "@/enums/level";

const {RangePicker} = DatePicker;

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any,
  changePage: boolean,
}

const exportColumns = [
  {name: 'tpmDetailId', displayName: '活动明细编号*',},
  {
    name: 'rowNumber',
    displayName: '行号',
    type: Enum.FieldType.NUMBER,
    dataType: Enum.FieldDataType.INT,
  },
  {name: 'boName', displayName: '四级使用部门'},
  {name: 'soldToCode', displayName: '客户'},
  {name: 'shipToCode', displayName: '收货地编号(用户填写，票扣时需填写)'},
  {name: 'shopName', displayName: '门店'},
  {name: 'productBuyNames', displayName: '产品别'},
  {name: 'ecName', displayName: '费用项'},
  {name: 'revenueTypeName', displayName: '核算类型'},
  {name: 'paymentMethodName', displayName: '支付方式'},
  {name: 'amountSa', displayName: '申请金额'},
  {name: 'amountActual', displayName: '系统计算金额'},
  {name: 'amountPaid', displayName: '已核销金额'},
  {name: 'amountNotPaid', displayName: '可核销金额'},
  {name: 'quantityPosForecast', displayName: '预计POS销量'},
  {name: 'revenuePosForecast', displayName: '预计POS销售额'},
  {name: 'quantityPosActualKeyin', displayName: '实际POS销量(箱/大袋)(用户填写)'},
  {name: 'revenuePosActualKeyin', displayName: '实际POS销售额(用户填写)'},
  {name: 'amountInVat', displayName: '核销金额(含税)*(用户填写)'},
  {name: 'amountExInVat', displayName: '核销金额(不含税)(账扣/转账/第三方/记账时可填，不填则自动计算)'},
  {name: 'remark', displayName: '核销备注(用户填写)'},
  {name: 'activityName', displayName: '活动名称'},
  {name: 'activityStartDate', displayName: '活动开始时间'},
  {name: 'activityEndDate', displayName: '活动结束时间'},
];

const SpSaDetailSelectApp: React.FC<WdiModalAppProps> = (props) => {
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
    },
    sorter: null,
    filterOptions: null,
    changePage: false,
  });
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const {formSp} = props.data;

  const buttonColumns: ButtonColumn[] = [
    {
      title: '导出',
      key: 'Export',
      onClick: async (filterOptions: any) => {
        const req: API.QueryForPageReq = {
          filterOptions: JSON.stringify(getFilterOptions(Object.assign({}, tableParams, {filterOptions}))),
          pagenum: 0,
          pagesize: 0
        };
        const response = await FormSpDetail.queryFormSpDetailsForSelect(req);
        const sheet = {
          data: response?.data?.records,
          names: exportColumns,
          sheetName: "FormSPApply",
          cells: {
            activityStartDate: (fieldObject: any, row: any, value: any) => {
              return wdiCore.Date.Join(row.activityStartDate, row.activityEndDate)
            },
          },
        };
        toExcel({
          name: "核销信息_" + wdiCore.Date.ToString(new Date()) + ".xlsx",
          sheetProps: [sheet]
        })
      }
    },
  ]

//查询条件包括：活动单号（模糊查询，默认），活动名称（模糊查询），专项项目
// 售达方（多选搜索）,费用类别（多选三层，可选择大类/小类/费用项），活动单提交日期（起止），活动期间（起止），门店（多选搜索）
  const filterColumns: FilterColumn[] = [
    {
      title: '活动单号',
      dataIndex: 'formNo',
      render: () => {
        return <Input allowClear/>
      }
    },
    {
      title: '活动名称',
      dataIndex: 'activityName',
      render: () => {
        return <Input allowClear/>
      }
    },
    {
      title: '专项项目',
      dataIndex: 'budgetProjectId',
      span: 4,
      render: () => {
        return (
          <WdiSelect {...{
            allowClear: true,
            placeholder: "请选择专项项目",
            showSearch: true,
            remote: true,
            dataLoading: async (params) => {
              const response = await BudgetProject.ajaxSearch(Object.assign({withPermissions: true}, params));
              return response.data.map((item) => {
                return {label: item.codeOrName, value: item.budgetProjectId}
              });
            },
          }} />
        )
      },
    },
    {
      title: '预算归属',
      dataIndex: 'bohqId',
      span: 4,
      render: () => {
        return (
          <WdiTreeSelect {...{
            placeholder: "请选择预算归属",
            multiple: true,
            dataLoading: async () => {
              return await BOHQ.search({
                bohqLevels: [LevelEnum.BOHqLevel.BO1, LevelEnum.BOHqLevel.BO2].join(),
                withPermissions: true,
                existLessRoot: true
              }).then((res) => {
                const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
                  textField: "bohqName",
                  valueField: "bohqId",
                  id: "bohqId",
                  pId: "parentId"
                }, res.data);
                return {treeData, dict}
              })
            },
          }} />
        )
      },
    },
    {
      title: '费用项',
      dataIndex: 'ecId',
      span: 4,
      render: () => {
        return (
          <WdiTreeSelect {...{
            placeholder: "请选择费用项",
            multiple: true,
            dataLoading: async (params) => {
              const response = await EC.searchEc3(Object.assign({
                toParentLevel: String(Enum.ECLevel.EC1),
                isActive: true
              }, params));
              const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
                textField: "ecName",
                valueField: "ecId",
                id: "ecId",
                pId: "parentId",
                level: "ecLevel",
              }, response.data);
              return {treeData, dict};
            },
          }} />
        )
      },
    },
    {
      title: '门店',
      dataIndex: 'shopId',
      span: 4,
      render: () => {
        return (
          <WdiSelect {...{
            placeholder: "请选择门店",
            mode: "multiple",
            showSearch: true,
            remote: true,
            dataLoading: async (params) => {
              const response = await Shop.ajaxSearch(Object.assign({
                isActive: true,
              }, params));
              return response.data.map((item) => ({value: item.shopId, label: item.codeOrName}));
            },
          }} />
        )
      },
    },
    {
      title: '活动单提交日期',
      dataIndex: 'submitDate',
      span: 8,
      render: () => {
        return <RangePicker style={{width: '100%'}}/>
      }
    },
    {
      title: '活动期间',
      dataIndex: 'activityStartDate',
      span: 8,
      render: () => {
        return <RangePicker style={{width: '100%'}}/>
      }
    },
  ];
//默认列：活动明细ID，活动名称，活动期间，售达方，门店，产品别，费用项，支付方式，申请金额，可核销金额
  const columns: ColumnsType<any> = [
    {
      title: '活动明细ID',
      width: 80,
      dataIndex: 'tpmDetailId',
      ellipsis: true,
    },
    {
      title: '活动名称',
      width: 80,
      dataIndex: 'activityName',
      ellipsis: true,
    },
    {
      title: '活动期间',
      width: 80,
      dataIndex: 'activityDate',
      ellipsis: true,
      render: (_, record) => (
        wdiCore.Date.Join(record.activityStartDate, record.activityEndDate)
      ),
    },
    {
      title: '四级使用部门',
      width: 100,
      dataIndex: 'boName',
      ellipsis: true,
    },
    {
      title: '客户',
      width: 120,
      dataIndex: 'soldToCode',
      ellipsis: true,
      render: (_, record) => (
        wdiCore.String.CodeOrName(record.soldToCode, record.soldToName)
      ),
    },
    {
      title: '门店',
      width: 120,
      dataIndex: 'shop',
      ellipsis: true,
      render: (_, record) => (
        wdiCore.String.CodeOrName(record.shopCode, record.shopName)
      ),
    },
    {
      title: '产品别',
      width: 120,
      dataIndex: 'productBuyNames',
      ellipsis: true,
    },
    {
      title: '费用项',
      width: 100,
      dataIndex: 'ecName',
      ellipsis: true,
    },
    {
      title: '申请金额',
      width: 80,
      dataIndex: 'amountSa',
      ellipsis: true,
    },
    {
      title: '可核销金额',
      width: 80,
      dataIndex: 'amountNotPaid',
      ellipsis: true,
    },
  ];

  useEffect(() => {
    fetchData(Object.assign({}, tableParams));
  }, []);

  const getFilterOptions = (params: TableParams) => {
    const filterOptions: any = {};
    filterOptions.paymentMethodId = formSp.paymentMethodId;
    if (props.data.dataSource && props.data.dataSource.length > 0) {
      filterOptions.excludeFormSaDetailIds = props.data.dataSource.map((item: any) => item.formSaDetailId).join()
    }
    if (Enum.PaymentMethod.BILL_DEDUCT == formSp.paymentMethodId
      || Enum.PaymentMethod.FEE_POOL == formSp.paymentMethodId
      || Enum.PaymentMethod.FG_POOL == formSp.paymentMethodId
      || Enum.PaymentMethod.BUY_GIVE_POOL == formSp.paymentMethodId
      || Enum.PaymentMethod.ACCOUNTING_ACCRUAL == formSp.paymentMethodId
      || Enum.PaymentMethod.ACCOUNTING_NO_ACCRUAL == formSp.paymentMethodId
      || Enum.PaymentMethod.ACCOUNT_DEDUCT == formSp.paymentMethodId) {
      filterOptions.soldToCode = formSp.soldToCode ?? undefined
    } else if (Enum.PaymentMethod.TRANSFER == formSp.paymentMethodId) {
      filterOptions.soldToCode = formSp.soldToCode ?? undefined
      filterOptions.companyCode = formSp.companyCode ?? undefined;
    } else if (Enum.PaymentMethod.THIRD_PART == formSp.paymentMethodId) {
      filterOptions.companyCode = formSp.companyCode ?? undefined;
    }
    if (params.filterOptions) {
      const {
        formNo, activityName, budgetProjectId, bohqId, ecId, shopId, submitDate, activityStartDate
      } = params.filterOptions;
      filterOptions.formNo = formNo;
      filterOptions.activityName = activityName;
      filterOptions.budgetProjectId = budgetProjectId;
      if (!wdiCore.Object.IsNullOrUndefined(bohqId) && bohqId.length > 0) {
        filterOptions.bohqId = WdiSelectFormat.getvalue(bohqId);
      }
      if (!wdiCore.Object.IsNullOrUndefined(ecId) && ecId.length > 0) {
        filterOptions.ecId = WdiSelectFormat.getvalue(ecId);
      }
      if (!wdiCore.Object.IsNullOrUndefined(shopId) && shopId.length > 0) {
        filterOptions.shopId = shopId.join();
      }
      if (!wdiCore.Object.IsNullOrUndefined(submitDate)) {
        filterOptions.submitDate = submitDate.map((item: any) => {
          return moment(item).format('YYYY-MM-DD')
        }).join();
      }
      if (!wdiCore.Object.IsNullOrUndefined(activityStartDate)) {
        filterOptions.activityStartDate = activityStartDate.map((item: any) => {
          return moment(item).format('YYYY-MM-DD')
        }).join();
      }
      wdiCore.Object.RemoveNullOrUndefined(filterOptions);
    }
    return filterOptions;
  }

  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions = getFilterOptions(params);
    const req: API.QueryForPageReq = {
      filterOptions: JSON.stringify(filterOptions),
      pagenum: (params.changePage && params.pagination.current) ? (params.pagination.current - 1) : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
    };
    FormSpDetail.queryFormSpDetailsForSelect(req).then(responseResult => {
      const data = responseResult.data;
      if (data) {
        setDataSource(data.records);
        setTableParams({
          changePage: false,
          pagination: {
            ...params.pagination,
            total: data.totalCount,
            current: params.changePage ? params.pagination.current : 1
          },
          sorter: params.sorter,
          filterOptions: params.filterOptions
        });
        setLoading(false);
        setSelectedRowKeys([]);
      }
    });
  };
  const doSearch = (filterOptions: any) => {
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  }

  const onSelectChange = (newSelectedRowKeys: Key[], newSelectedRows: any[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };
  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      onCancel={() => {
        props.onClose?.()
      }}
      confirmLoading={btnLoading}
      onOk={() => {
        if (selectedRows.length == 0) {
          message.error('请至少选择一条数据').then();
        } else {
          setBtnLoading(true);
          FormSpDetail.getFormSpDetailsForSaApplySelect(selectedRows.map((item: any) => (item.formSaDetailId)).join(",")).then(resp => {
            props.onSave?.({
              code: ResponseStatus.SUCCESS,
              data: resp.data
            });
            setBtnLoading(false);
          });
        }
      }}
    >
      <GridFilterForm doSearch={doSearch} columns={filterColumns} buttonColumns={buttonColumns}/>
      <WdiTable
        showPageRows={10}
        rowSelection={{selectedRowKeys, onChange: onSelectChange}}
        columns={columns}
        size='small'
        dataSource={dataSource}
        pagination={{
          ...tableParams.pagination,
          onChange: (page, pageSize) => {
            fetchData(Object.assign({}, tableParams, {
              changePage: true,
              pagination: {
                ...tableParams.pagination,
                current: page,
                pageSize: pageSize
              }
            }))
          }
        }}
        loading={loading}
      />
    </WdiModal>
  );
};
export default SpSaDetailSelectApp;
